/** @jsx jsx */
import React, { Fragment } from 'react'
import { jsx } from '@emotion/core'
import { observer } from 'mobx-react'
import _ from 'lodash'
import useRootStore from '@store/useRootStore'
import { HOME_OR_AWAY_SCORE } from '@constants/index'
import dayjs from 'dayjs'
import { GAME_TYPE } from '@game-type/game-market-types'
import { IframeWrap, LiveScoreIframeWrap, TxtLimit } from '@styles/base.style'
import HomeTagImg from '@assets/images/icon/matchup_icon/home-tag-ico.png'
import Iframe from 'react-iframe'
import { isMobile } from 'react-device-detect'
import {
  convertRate,
  getHomeOrAwayLiveScoreWithPeriodScores,
  isLsportsEnv,
  isShowMatchRecordButton,
} from '@utils/utils'
import { LS_SPORT_ID, SPORT_ID } from 'adv-protocol/src/GameCommon'
import { DetailMatchTrack, MatchTrack } from '@store/matchupStore'
import { GamePreviewWrap, MatchTeamInfoWrap, MatchWrapper, normalBet, straightBet } from '@styles/match.style'
import { LS_SPORT_ID_ICON_MAP, SPORT_ID_ICON_MAP } from '@shared/SportIdGroup'
import LeagueEmblemImg from '@assets/images/icon/emblem_icon/league.png'
import { BASIC_TEAM_EMBLEM_MAP } from '@shared/EmblemGroup'
import { LS_SPORTS_TYPE_NUM, PAGE_TYPE, SPORTS_TYPE_NUM } from '@interface/common'
import { MatchChart } from '@views/Home/index.style'
import LiveSoccerBg from '@assets/images/bg/match/soccer.jpg'
import LiveBasketballBg from '@assets/images/bg/match/basketball.jpg'
import LiveBaseballBg from '@assets/images/bg/match/baseball.jpg'
import LiveIceHokeyBg from '@assets/images/bg/match/ice_hockey.jpg'
import LiveVolleyballBg from '@assets/images/bg/match/volleyball.jpg'
import LiveTableTennisBg from '@assets/images/bg/match/table_tennis.jpg'
import LiveDefaultBg from '@assets/images/bg/match/common_sports.jpg'
import { ArrDayText } from '@game/util'
import { makeVolleyballScore } from '@utils/jsxutils'
import classNames from 'classnames'
import { MatchSimpleInfoWrap, VisualNotice } from '@views/Matchup/MatchUpDetail/index.style'
import { ToolTipIco } from '@shared/Icon/MatchupIcon'
import LiveVisualNotice from '@views/Matchup/MatchUpDetail/LiveVisualNotice'
import { Schema$Outcome } from '@protocol/game'
import { Schema$UserPickWayRatio } from '@interface/user-pick'
import { MatchRecordButton } from '@components/Button/MatchRecordButton'

export interface SimpleMatchInfoProps {
  outcomes: Schema$Outcome[]
  wayRatio: Schema$UserPickWayRatio
  visualSupport: boolean
  liveScoreIframeWrapClassName: string
  simpleMatchInfoClassName: string
  iframeWrapTypeClassName: string
}

export const SimpleMatchInfo: React.FC<SimpleMatchInfoProps> = observer(props => {
  const { globalStore, matchupStore, authStore } = useRootStore()

  const {
    outcomes,
    wayRatio,
    visualSupport,
    liveScoreIframeWrapClassName,
    simpleMatchInfoClassName,
    iframeWrapTypeClassName,
  } = props

  const {
    currentGameKey,
    detailTrack,
    pageType,
  } = matchupStore

  const {
    sportId = 178,
    categoryName,
    tournamentName = '리그명',
    tournamentEmblem,
  } = detailTrack || ({} as DetailMatchTrack)

  const track = detailTrack?.track ?? ({} as MatchTrack)
  const {
    matchId,
    dateOfMatch = new Date(),
    homeTeamName = '홈팀',
    awayTeamName = '원정팀',
    homeTeamEmblem,
    awayTeamEmblem,
    feedSupplier,
  } = track

  let iframeWrapClassName
  if (isMobile) {
    if (currentGameKey === GAME_TYPE.LIVE) {
      iframeWrapClassName = 'detail-bet-iframe live-extended'
    } else {
      iframeWrapClassName = 'detail-bet-iframe'
    }
  } else {
    // eslint-disable-next-line no-lonely-if
    if (currentGameKey === GAME_TYPE.LIVE) {
      iframeWrapClassName = 'detail-bet-iframe live-extended pc'
    } else {
      iframeWrapClassName = 'detail-bet-iframe pc'
    }
  }

  const isBaseball = (sportId === SPORT_ID.BASEBALL || sportId === LS_SPORT_ID.BASEBALL)
  const isTableTennis = visualSupport && sportId === SPORT_ID.TABLE_TENNIS

  let homeTagClass
  let homeTagBaseballClass
  if (isBaseball) {
    homeTagBaseballClass = 'baseball'
  } else if (isTableTennis && currentGameKey === GAME_TYPE.LIVE) {
    homeTagBaseballClass = 'table-tennis'
  }

  if (isMobile) {
    homeTagClass = 'home-tag-img'
    if (isTableTennis) {
      homeTagClass = 'home-tag-img table-tennis'
    }
  } else {
    homeTagClass = 'home-tag-img pc'
    if (isTableTennis) {
      homeTagClass = 'home-tag-img pc table-tennis'
    }
  }

  // matchup.scoreboard라이센스 관련(벳레이더) 작업이 시간이 소요됨으로
  // 기존 비주얼라이제이션에서 상세스코어를 라이브일 경우에만 보이도록 적용
  // matchup.scoreboard라이센스 해결 된 후에 다시 작업 필요
  let convertedUrl = globalStore.getVisualizationUrl(matchId, sportId, currentGameKey, feedSupplier, detailTrack)
  if (currentGameKey === GAME_TYPE.LIVE) {
    convertedUrl = convertedUrl.replace('&momentum=disable', '').concat('&detailedScoreboard=scoreDetails')
  }

  // 라이브 뒷배경
  let matchGroupBgImage
  switch (sportId) {
    case LS_SPORTS_TYPE_NUM.SOCCER:
    case SPORTS_TYPE_NUM.SOCCER:
      matchGroupBgImage = LiveSoccerBg
      break
    case LS_SPORTS_TYPE_NUM.BASKETBALL:
    case SPORTS_TYPE_NUM.BASKETBALL:
      matchGroupBgImage = LiveBasketballBg
      break
    case LS_SPORTS_TYPE_NUM.BASEBALL:
    case SPORTS_TYPE_NUM.BASEBALL:
      matchGroupBgImage = LiveBaseballBg
      break
    case LS_SPORTS_TYPE_NUM.ICE_HOCKEY:
    case SPORTS_TYPE_NUM.ICE_HOCKEY:
      matchGroupBgImage = LiveIceHokeyBg
      break
    case LS_SPORTS_TYPE_NUM.VOLLEYBALL:
    case SPORTS_TYPE_NUM.VOLLEYBALL:
      matchGroupBgImage = LiveVolleyballBg
      break
    case SPORTS_TYPE_NUM.TABLE_TENNIS:
      matchGroupBgImage = LiveTableTennisBg
      break
    default:
      matchGroupBgImage = LiveDefaultBg
      break
  }

  const dateInst = new Date(dateOfMatch)
  const dateText = dayjs(dateOfMatch).format('MM/DD')
  const monthText = ArrDayText[new Date(dateInst).getDay()]
  const dateOfMatchText = `${dateText}(${monthText})`
  const timeText = dayjs(dateOfMatch).format('HH:mm')

  let matchStatusLabel = ''   // 필요없음
  let homeClassName = ''
  let awayClassName = ''
  let homeScoreNumClassName = ''
  let awayScoreNumClassName = ''
  let hScore = 0
  let aScore = 0
  let live_hScore = '-'
  let live_aScore = '-'
  let eleHomeScore = []
  let eleAwayScore = []
  let styleAwayScore
  let styleHomeScore

  if (track) {
    const { awayScore, homeScore, matchStatusName } = track
    matchStatusLabel = matchStatusName

    // NOTE: 라이브 상세 베팅 배구의 경우 세트별 점수를 보여주기위한 로직
    // 실시간으로 최종세트 스코어만 뿌려주면 되기때문에 제일 마지막 배열의 데이타를 뿌려준다
    live_hScore = getHomeOrAwayLiveScoreWithPeriodScores(
      HOME_OR_AWAY_SCORE.HOME,
      track,
      sportId,
      currentGameKey === GAME_TYPE.LIVE,
    )
    live_aScore = getHomeOrAwayLiveScoreWithPeriodScores(
      HOME_OR_AWAY_SCORE.AWAY,
      track,
      sportId,
      currentGameKey === GAME_TYPE.LIVE,
    )

    hScore = homeScore
    aScore = awayScore

    if (sportId === SPORTS_TYPE_NUM.VOLLEYBALL
      || sportId === LS_SPORTS_TYPE_NUM.VOLLEYBALL
      || sportId === SPORTS_TYPE_NUM.TABLE_TENNIS) {
      const volleyballHomeScore = makeVolleyballScore(live_hScore)
      const volleyballAomeScore = makeVolleyballScore(live_aScore)
      live_hScore = volleyballHomeScore.periodScore
      eleHomeScore = volleyballHomeScore.setScore
      live_aScore = volleyballAomeScore.periodScore
      eleAwayScore = volleyballAomeScore.setScore
    }

    styleAwayScore = _.toNumber(pageType === PAGE_TYPE.MATCHUP_RESULT
    || pageType === PAGE_TYPE.MATCHUP_RESULT_FILTER ? aScore : live_aScore)
    styleHomeScore = _.toNumber(pageType === PAGE_TYPE.MATCHUP_RESULT
    || pageType === PAGE_TYPE.MATCHUP_RESULT_FILTER ? hScore : live_hScore)

    if (styleHomeScore > styleAwayScore) {
      homeClassName = 'win'
      awayClassName = 'lose'
      homeScoreNumClassName = 'win'
    } else if (styleHomeScore < styleAwayScore) {
      homeClassName = 'lose'
      awayClassName = 'win'
      awayScoreNumClassName = 'win'
    }

    if (_.isNaN(styleHomeScore) || _.isNaN(styleAwayScore)) {
      styleHomeScore = '-'
      styleAwayScore = '-'
    }
  }

  let homeRate
  let drawRate
  let awayRate

  let isTwoWay = false

  if (!_.isNil(outcomes)) {
    if (outcomes.length === 2) {
      isTwoWay = true
      homeRate = outcomes[0].rate
      awayRate = outcomes[1].rate
    } else if (outcomes.length === 3) {
      homeRate = outcomes[0].rate
      drawRate = outcomes[1].rate
      awayRate = outcomes[2].rate
    }
  }

  const winRatio = wayRatio?.home
  const drawRatio = wayRatio?.draw
  const loseRatio = wayRatio?.away

  const oddsClass = classNames('odd-rate result-page', {
    'is-draw-score': !isTwoWay,
    'none': _.isNil(homeRate) && _.isNil(drawRate) && _.isNil(awayRate),
    'adv-hidden': currentGameKey === GAME_TYPE.LIVE,
  })

  const isDrawScore = !isTwoWay

  const isHomeWin = outcomes?.[0]?.result ?? false
  const isAwayWin = isTwoWay ? (outcomes?.[1]?.result ?? false) : (outcomes?.[2]?.result ?? false)
  const isDrawWin = isTwoWay ? false : (outcomes?.[1]?.result ?? false)

  const detailBetClassName = classNames('detail-bet', {
    'result-page': pageType === PAGE_TYPE.MATCHUP_RESULT || pageType === PAGE_TYPE.MATCHUP_RESULT_FILTER,
  })

  let gameInfoCenterElem

  if (currentGameKey === GAME_TYPE.LIVE) {
    gameInfoCenterElem = (
      <Fragment>
        {/* ********* 라이브 상세 BET ********* */}
        <div className="score">
          <span className={`home-score ${homeClassName}`}>
            {(sportId === SPORT_ID.VOLLEYBALL || sportId === LS_SPORT_ID.VOLLEYBALL) &&
              <div className="volleyball-score home">
                {/* 스코어 만큼 갯수를 늘려주시면 됩니다. */}
                {eleHomeScore}
              </div>
            }
            {pageType === PAGE_TYPE.MATCHUP_RESULT ? hScore : live_hScore}
          </span>
          <span className="center">:</span>
          <span className={`away-score ${awayClassName}`}>
            {(sportId === SPORT_ID.VOLLEYBALL || sportId === LS_SPORT_ID.VOLLEYBALL) &&
              <div className="volleyball-score away">
                {/* 스코어 만큼 갯수를 늘려주시면 됩니다. */}
                {eleAwayScore}
              </div>
            }
            {pageType === PAGE_TYPE.MATCHUP_RESULT ? aScore : live_aScore}
          </span>
        </div>
        <span className="status">{matchStatusLabel}</span>
      </Fragment>
    )
  } else {
    if (pageType === PAGE_TYPE.MATCHUP_RESULT || pageType === PAGE_TYPE.MATCHUP_RESULT_FILTER) {
      gameInfoCenterElem = (
        <Fragment>
          {/* ********* 경기결과 ********* */}
          <div className="date-info-wrap">
            <span className="date">{dateOfMatchText}</span>
            <span className="time">{timeText}</span>
          </div>
          <div className="score">
            <span className={`home-score ${homeClassName}`}>
              {hScore}
            </span>
            <span className="center"/>
            <span className={`away-score ${awayClassName}`}>
              {aScore}
            </span>
          </div>
          <div className="dividend-board">
            <span className={oddsClass} css={isHomeWin ? straightBet : normalBet}>
              {convertRate(authStore.isSignedIn, homeRate)}
            </span>
            {isDrawScore && (
              <span className={oddsClass} css={isDrawWin ? straightBet : normalBet}>
                {convertRate(authStore.isSignedIn, drawRate)}
              </span>
            )}
            <span className={oddsClass} css={isAwayWin ? straightBet : normalBet}>
              {convertRate(authStore.isSignedIn, awayRate)}
            </span>
          </div>
        </Fragment>
      )
    } else {
      gameInfoCenterElem = (
        <Fragment>
          {/* ********* 일반 상세 BET ********* */}
          <span className="date">{dateOfMatchText}</span>
          <span className="time">{timeText}</span>
          <div className="dividend-board">
                <span className={oddsClass} css={isHomeWin ? straightBet : normalBet}>
                  {convertRate(authStore.isSignedIn, homeRate)}
                </span>
            {isDrawScore && (
              <span className={oddsClass} css={isDrawWin ? straightBet : normalBet}>
                    {convertRate(authStore.isSignedIn, drawRate)}
                  </span>
            )}
            <span className={oddsClass} css={isAwayWin ? straightBet : normalBet}>
                  {convertRate(authStore.isSignedIn, awayRate)}
                </span>
          </div>
        </Fragment>
      )
    }
  }

  const eleGamePreview = (
    <GamePreviewWrap id="gamePreviewWrap" className="game-preview-wrap">
      <MatchWrapper>
        <div className="match-block-header match-detail">
          <div className={currentGameKey === GAME_TYPE.LIVE ? 'tournament-info live' : 'tournament-info'}>
            <div className="tournament-wrap">
              {isLsportsEnv() ? LS_SPORT_ID_ICON_MAP[sportId] : SPORT_ID_ICON_MAP[sportId]}
              <span className="category-name">{categoryName}</span>
              <img src={tournamentEmblem || LeagueEmblemImg} alt="토너먼트 엠블럼" className="tournament-emblem"/>
              <span className="tournament-name" css={TxtLimit}>
                {tournamentName}
              </span>
            </div>
          </div>
        </div>
      </MatchWrapper>

      <MatchTeamInfoWrap className={detailBetClassName}>
        {/* ********* 라이브일 경우 배경 ********* */}
        {currentGameKey === GAME_TYPE.LIVE &&
          <div className="live-detail-bg">
            <img src={matchGroupBgImage} alt="종목 라이브 배경" className="live-detail-bg--img"/>
          </div>
        }

        <div className="game-info-team home">
          <img src={HomeTagImg} alt="홈팀" className={`${homeTagClass} ${homeTagBaseballClass}`}/>
          <img
            src={homeTeamEmblem || BASIC_TEAM_EMBLEM_MAP[sportId]}
            alt="home team emblem"
            className="emblem"
          />
          <span className="team-name">{homeTeamName}</span>
        </div>

        <div className="game-info-center">
          {gameInfoCenterElem}
        </div>
        <div className="game-info-team away">
          <img
            src={awayTeamEmblem || BASIC_TEAM_EMBLEM_MAP[sportId]}
            alt="away team emblem"
            className="emblem"
          />
          <span className="team-name">{awayTeamName}</span>
        </div>

        {(winRatio > 0 || drawRatio > 0 || loseRatio > 0) && (
          <MatchChart className="game-info-match-chart">
            <div className="chart-info">
              {winRatio > 0 && <p className="chart-info-name win">
                승 {winRatio}%
              </p>}
              {drawRatio > 0 && <p className="chart-info-name draw">
                무 {drawRatio}%
              </p>}
              {loseRatio > 0 && <p className="chart-info-name lose">
                패 {loseRatio}%
              </p>}
            </div>
            <div className="chart-bar">
              {winRatio > 0 && <span className="chart-item-value win" style={{ flex: winRatio }}/>}
              {drawRatio > 0 && <span className="chart-item-value draw" style={{ flex: drawRatio }}/>}
              {loseRatio > 0 && <span className="chart-item-value lose" style={{ flex: loseRatio }}/>}
            </div>
          </MatchChart>
        )}
        {isShowMatchRecordButton(currentGameKey, pageType, sportId) && (
          <MatchRecordButton
            pageType={pageType}
            matchRecordSimpleMatchInfo={{
              matchId,
              sportId,
              homeScore: hScore,
              awayScore: aScore,
              homeTeamName,
              awayTeamName,
              homeTeamEmblem,
              awayTeamEmblem,
              matchStatusName: matchStatusLabel,
              isLiveMatch: currentGameKey === GAME_TYPE.LIVE,
            }}
          />
        )}
      </MatchTeamInfoWrap>
    </GamePreviewWrap>
  )

  const onClickDetailNoticePopup = () => {
    globalStore.pushDialogOk({
      title: '유의사항',
      custom: <LiveVisualNotice/>,
      className: 'big-size box-type',
      // 뒷배경 클릭 시 닫히도록
      callbackCloseDialogBg: () => {
        globalStore.closeDialog()
      },
    })
  }

  let notice
  if (currentGameKey === GAME_TYPE.LIVE && visualSupport) {
    notice = (
      <VisualNotice onClick={onClickDetailNoticePopup} className="cursor-pointer">
        <ToolTipIco/>위 정보는 실제 경기상황과 다를 수 있습니다.
      </VisualNotice>
    )
  }

  /** eleVisualSupport 출력 조건
   * 기존 : visualSupport = true일 때, 기존 비주얼라이제이션 출력
   *       visualSupport = false일 때, GamePreviewWrap 출력
   * 수정 : currentGameKey === GAME_TYPE.LIVE 일 때, visualSupport = true라면 기존 비주얼라이제이션 출력
   *       currentGameKey === GAME_TYPE.LIVE 일 때, visualSupport = false라면 GamePreviewWrap 출력
   *       currentGameKey !== GAME_TYPE.LIVE 일 때, GamePreviewWrap 출력
   * */
  const eleVisualSupport = (currentGameKey === GAME_TYPE.LIVE ?
      <Fragment>
        {visualSupport &&
          <Fragment>
            {/* 기존 비주얼라이제이션 */}
            <IframeWrap id="betIframeWrap" className={`${iframeWrapClassName} ${iframeWrapTypeClassName}`}>
              <img src={HomeTagImg} alt="홈팀" className={`${homeTagClass} ${homeTagBaseballClass}`}/>
              <Iframe url={convertedUrl} id="extendedIframe" scrolling="no" frameBorder={0}
                      className={currentGameKey === GAME_TYPE.LIVE && 'live-iframe'}/>
            </IframeWrap>

            {/* 라이브 - 스크롤 시 나타나는 스코어 영역 iframe */}
            {currentGameKey === GAME_TYPE.LIVE &&
              <LiveScoreIframeWrap id="betIframeScoreWrap"
                                   className={`${liveScoreIframeWrapClassName} ${iframeWrapTypeClassName} ${homeTagBaseballClass}`}>
                <img src={HomeTagImg} alt="홈팀" className={`${homeTagClass} ${homeTagBaseballClass}`}/>
                <Iframe url={convertedUrl} scrolling="no" frameBorder={0}/>
              </LiveScoreIframeWrap>
            }
          </Fragment>
        }
        {!visualSupport &&
          eleGamePreview
        }
      </Fragment>
      :
      eleGamePreview
  )
  return <Fragment>
    {detailTrack && eleVisualSupport}
    {notice}
    <MatchSimpleInfoWrap className={simpleMatchInfoClassName}>
      <span className="bg"/>
      <div className="simple-info">
        <img src={HomeTagImg} alt="홈팀" className="home-tag-img"/>
        <span className="simple-info--home-team">
                  <img
                    src={homeTeamEmblem || BASIC_TEAM_EMBLEM_MAP[sportId]}
                    alt="home team emblem"
                    className="emblem"
                  />
                  <span className="team-name">{homeTeamName}</span>
                </span>
        {currentGameKey !== GAME_TYPE.LIVE && <span className="simple-info--vs">vs</span>}
        {(currentGameKey === GAME_TYPE.LIVE
            || pageType === PAGE_TYPE.MATCHUP_RESULT
            || pageType === PAGE_TYPE.MATCHUP_RESULT_FILTER)
          && <span className="simple-info--score">
                  <span className={`${homeScoreNumClassName} score-num home`}>
                    {styleHomeScore}
                  </span>
                  <span className="score-center">:</span>
                  <span className={`${awayScoreNumClassName} score-num away`}>
                    {styleAwayScore}
                  </span>
                </span>
        }
        <span className="simple-info--away-team">
                  <span className="team-name">{awayTeamName}</span>
                  <img
                    src={awayTeamEmblem || BASIC_TEAM_EMBLEM_MAP[sportId]}
                    alt="away team emblem"
                    className="emblem"
                  />
                </span>
      </div>
    </MatchSimpleInfoWrap>
  </Fragment>
})
